// src/pages/SignupPage.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useNotification } from '../contexts/NotificationContext';
import { mapErrorKey } from '../utils/errorMapping';
import { Button } from 'baseui/button';
import { Input } from 'baseui/input';
import { FormControl } from 'baseui/form-control';
import { HeadingLarge } from 'baseui/typography';
import {
  PageContainer,
  SectionContainer,
  FlexColumnCentered
} from '../styles/controllerPageStyles';
import {
  ErrorMessage,
  AuthButtonContainer,
  AuthLinkContainer
} from '../styles/authPageStyles';
import PageFooter from '../components/common/PageFooter';

const SignupPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const { t } = useLocale();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError(t('errors.passwordsDoNotMatch'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      await signup(email, password);
      setError(t('auth.checkEmailForConfirmation'));
    } catch (err) {
      const errorKey = err.message || 'UNKNOWN_ERROR';
      const clientKey = errorKey.startsWith('errors.') ? errorKey : mapErrorKey(errorKey);
      setError(t(clientKey));
      showNotification(t(clientKey), 'error');
    }
    setLoading(false);
  };

  return (
    <PageContainer>
      <FlexColumnCentered>
        <HeadingLarge>{t('pages.signup.title')}</HeadingLarge>
      </FlexColumnCentered>
      <SectionContainer>
        <form onSubmit={handleSubmit}>
          <FormControl label={() => t('common.emailLabel')}>
            <Input
              id="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </FormControl>
          <FormControl label={() => t('common.passwordLabel')}>
            <Input
              id="password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </FormControl>
          <FormControl label={() => t('common.confirmPasswordLabel')}>
            <Input
              id="confirm-password-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </FormControl>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <AuthButtonContainer>
            <Button type="submit" isLoading={loading}>
              {t('common.signUpButton')}
            </Button>
          </AuthButtonContainer>
        </form>
        <AuthLinkContainer>
          {t('pages.signup.alreadyHaveAccount')}
          {' '}
          <Button onClick={() => navigate('/login')} kind="tertiary" size="compact">
            {t('pages.login.button')}
          </Button>
        </AuthLinkContainer>
      </SectionContainer>
      <PageFooter />
    </PageContainer>
  );
};

export default SignupPage;
